<template>
<v-container>
  <template v-if="hasNoData">
    {{ noDataText }}
  </template>
  <template v-else>
    <v-row wrap>
      <v-col class="black--text">
        Total Premium: {{ formattedTotalPremium }}
      </v-col>
      <v-col v-if="options.length > 2" cols="12" class="py-0">
        <v-select v-model="selectedOption" :items="options" label="Filter Schedules" hide-details @focus="sendFilterEvent">
          <template slot="item" slot-scope="data">
              <v-icon v-if="data.item.text.startsWith('Location')" class="blue3--text">mdi-map</v-icon>
              {{ data.item.text }}
          </template>
          <template slot="selection" slot-scope="data">
              <v-icon v-if="data.item.text.startsWith('Location')" class="blue3--text">mdi-map</v-icon>
              {{ data.item.text }}
          </template>
        </v-select>
      </v-col>
    </v-row>
    <template v-if="selectedOption <= 0">
      <template v-for="(schedule, c1) in formattedSchedules.schedules">
        <v-row :key="`cov-${c1}`">
          <v-col cols="12" class="py-1">
            <ScheduleCard :schedule="schedule" @openScheduledItemsDialog="openScheduledItemsDialog(schedule.id)" />
          </v-col>
        </v-row>
      </template>
    </template>
    <template v-for="(location, l) in formattedSchedules.locationSchedules">
      <template v-if="selectedOption === -1 || selectedOption === location.number">
      <!-- Location Name Row -->
        <v-row v-if="selectedOption === -1" :key="`loc-${l}`" class="locationRow">
          <v-col class="subtitle-1 py-0">
            <v-icon class="blue3--text">mdi-map</v-icon>
            <span>Location {{ location.number }}</span>
            <span v-if="(location.name || null) !== null && location.name.trim() !== ''"> - {{ location.name }}</span>
          </v-col>
        </v-row>
        <template v-for="(locSched, lc1) in location.schedules">
          <v-row :key="`locSched-${location.number}-${lc1}`" wrap>
            <v-col cols="12" class="py-1">
            <ScheduleCard :schedule="locSched" @openScheduledItemsDialog="openScheduledItemsDialog(locSched.id, location.id)" />
            </v-col>
          </v-row>
        </template>
      </template>
    </template>
  </template>

  <!-- Dialog to see Scheduled Items for a Schedule -->
  <v-dialog v-model="scheduleSelected" fullscreen scrollable hide-overlay transition="dialog-bottom-transition">
    <ScheduledItemsCard :scheduled-items="selectedScheduleScheduledItems" :type="scheduleType" @closeScheduleItemsDialog="closeScheduledItemsDialog" />
  </v-dialog>
  <v-dialog v-model="loading" hide-overlay persistent width="300">
    <v-card color="primary" dark>
      <v-card-text>
        Loading schedules...
        <v-progress-linear indeterminate color="white" class="mb-0" />
      </v-card-text>
    </v-card>
  </v-dialog>
</v-container>
</template>

<style scoped>
</style>

<script>
import ScheduleCard from '@/components/Client/LineOfBusiness/Schedules/ScheduleCard.vue';
import ScheduledItemsCard from '@/components/Client/LineOfBusiness/Schedules/ScheduledItemsCard.vue';
import apiService from '@/services/shared/agentPortalApiService.js';
import displayFormatterService from '@/services/shared/displayFormatterService.js';
import scheduleTranslator from '@/services/clientViewer/scheduleTranslator.js';
import analyticsService from '@/services/shared/analyticsService.js';
import { mapState, mapActions } from 'vuex';

export default {
  name: 'Schedules',
  components: {
    ScheduleCard, ScheduledItemsCard
  },
  props: {
    scheduleType: {
      type: Number,
      required: true
    }
  },
  data: function () {
    var options = [
      { text: 'All', value: -1 }
    ];

    options.push({ text: 'Line of Business', value: 0 });

    return {
      loading: true,
      totalPremium: 0,
      formattedSchedules: [],
      selectedOption: options[0].value,
      options: options,
      scheduleSelected: false,
      selectedScheduleId: null,
      selectedScheduleLocationId: null
    };
  },
  computed: {
    ...mapState(['selectedLineOfBusinessCode']),
    clientId: function () {
      return this.$route.params.clientId;
    },
    policyOrderNumber: function () {
      return this.$route.params.policyOrderNumber;
    },
    lobCode: function () {
      return this.selectedLineOfBusinessCode;
    },
    noDataText: function () {
      return this.error ? 'An error occurred fetching schedules' : (this.scheduleType === 1 ? 'This line of business has no schedules' : 'This line of business has no unscheduled schedules');
    },
    hasNoData: function () {
      if (this.loading) return false;
      if ((this.formattedSchedules || null) === null) return true;

      var noLobSchedules = (this.formattedSchedules.schedules || null) === null || this.formattedSchedules.schedules.length === 0;
      var noLocSchedules = (this.formattedSchedules.locationSchedules || null) === null || this.formattedSchedules.locationSchedules.length === 0;
      return noLobSchedules && noLocSchedules;
    },
    formattedTotalPremium: function () {
      return displayFormatterService.formatCurrency(this.totalPremium);
    },
    selectedSchedule: function () {
      if (this.selectedScheduleId === null) {
        return {};
      }
      if ((this.formattedSchedules.schedules || null) !== null) {
        var idx = this.formattedSchedules.schedules.findIndex(x => x.id === this.selectedScheduleId);
        if (idx !== -1) {
          return this.formattedSchedules.schedules[idx];
        }
      }

      if ((this.formattedSchedules.locationSchedules || null) !== null) {
        var locIdx = this.formattedSchedules.locationSchedules.findIndex(x => x.id === this.selectedScheduleLocationId);
        if (locIdx !== -1) {
          idx = this.formattedSchedules.locationSchedules[locIdx].schedules.findIndex(x => x.id === this.selectedScheduleId);
          if (idx !== -1) {
            return this.formattedSchedules.locationSchedules[locIdx].schedules[idx];
          }
        }
      }

      return {};
    },
    selectedScheduleScheduledItems: function () {
      if (this.scheduleType === 1) {
        if ((this.selectedSchedule.scheduledItems || null) === null) {
          return [];
        }
        return this.selectedSchedule.scheduledItems;
      } else {
        if ((this.selectedSchedule.unscheduledItems || null) === null) {
          return [];
        }
        return this.selectedSchedule.unscheduledItems;
      }
    }
  },
  created: async function () {
    this.loading = true;
    // 1 == ScheduledItems, 2 == UnscheduledItems
    var rs = await apiService.getSchedules(this.clientId, this.policyOrderNumber, this.lobCode, this.scheduleType);
    if (rs.code === 0) {
      if (rs.data.length === 0) this.schedules = [];
      this.formatSchedules(rs.data[0]);
    } else {
      this.error = true;
      this.setError(rs.message);
    }

    this.loading = false;
  },
  methods: {
    ...mapActions(['setError']),
    toggleView: function () {
      this.viewingMore = !this.viewingMore;
    },
    calculateTotalPremium: function (schedules) {
      schedules.forEach(x => {
        this.totalPremium += isNaN(x.premium) ? 0 : x.premium;
      });
    },
    formatSchedules: function (data) {
      this.totalPremium = 0;

      data = scheduleTranslator.translateSchedules(data);

      this.calculateTotalPremium(data.schedules);

      if ((data.locationSchedules || null) !== null) {
        data.locationSchedules.forEach(lh => {
          var locName = `Location ${lh.number}`;
          if ((lh.name || null) !== null && lh.name.trim() !== '') {
            locName += ` - ${lh.name}`;
          }

          this.options.push({ text: locName, value: lh.number });
          this.calculateTotalPremium(lh.schedules);
        });

        if (data.locationSchedules.length > 1) {
          data.locationSchedules.sort((l1, l2) => l1.number - l2.number);
        }
      }

      this.formattedSchedules = data;
    },
    openScheduledItemsDialog: function (id, locationId) {
      this.selectedScheduleId = id;
      this.selectedScheduleLocationId = locationId;
      this.scheduleSelected = true;
    },
    closeScheduledItemsDialog: function () {
      this.scheduleSelected = false;
      this.selectedScheduleId = null;
      this.selectedScheduleLocationId = null;
    },
    sendFilterEvent: function () {
      analyticsService.sendEvent(this.$gtag, 'Filter', 'Policy', 'Schedules filter');
    }
  }
};
</script>
